import { ISparqlExecutor } from "app/services/infrastructure/sparql-executor";
import { SparqlRecord, SparqlResultParser } from "app/services/infrastructure/sparql-result-parser";

export interface TreeRecord {
    expanded?: boolean;
    loading?: boolean;
    uri: string;
    childCount: number;
    depth: number;
    record: SparqlRecord;
    children?: TreeRecord[];
}

export interface TreeDataLoaderOptions {
    queryRoots: string;
    queryChildren: string;
}

export class TreeDataLoaderService {
    constructor(
        private sparqlExecutor: ISparqlExecutor,
        private options: TreeDataLoaderOptions,
        private currentTerm?: string
    ) {}

    async loadRootTreeLevel() {
        return await this.loadTreeLevel();
    }

    async loadRootTreeLevelWithSelectedNode(
        data: TreeRecord[],
        hierarchyPath?: string[]
    ): Promise<TreeRecord[]> {
        // If path and data are empty retrieve and return the root nodes only
        if ((!hierarchyPath && !data) || (data.length == 0 && hierarchyPath?.length == 0)) {
            return await this.loadTreeLevel();
        }

        // If data already exists and path is empty we don't need to retrieve the data again
        if (!hierarchyPath || hierarchyPath.length == 0) {
            return data;
        }

        // Retrieve the hierachy
        const roots = data && data.length > 0 ? data : await this.loadTreeLevel();
        let root = roots.find((x) => x.uri == hierarchyPath[0]);
        if (!root) {
            return roots;
        }
        root = await this.expandNode(root, hierarchyPath);
        return roots;
    }

    /**
     * Recursively expand nodes in the hierarchy path
     */
    expandNode = async (node: TreeRecord, hierarchyPath: string[]): Promise<TreeRecord> => {
        // Expand node if not already expanded
        if (!node.children || node.children.length == 0) {
            const children = await this.loadTreeLevel(node.uri, node.depth + 1);
            node.children = children;
            node.expanded = true;
        }

        // Remove current node from hierachy
        const newPath = hierarchyPath.slice(1);

        if (newPath.length == 0) {
            return node;
        }

        const nextNode = node.children.find((x) => x.uri == newPath[0]);

        if (!nextNode) {
            throw Error("Error retrieving node");
        }

        return this.expandNode(nextNode, newPath);
    };

    async expandTreeLevel(treeRecord: TreeRecord): Promise<void> {
        if (treeRecord.children && treeRecord.children.length > 0) {
            treeRecord.expanded = true;
            return;
        }

        if (treeRecord.childCount > 0) {
            treeRecord.loading = true;
            treeRecord.children = await this.loadTreeLevel(treeRecord.uri, treeRecord.depth + 1);
            treeRecord.expanded = true;
            treeRecord.loading = false;
        }
    }

    private async loadTreeLevel(term_iri: string = "", depth: number = 0): Promise<TreeRecord[]> {
        const options = this.options;
        const sparqlResult =
            depth == 0
                ? await this.sparqlExecutor.execute(options.queryRoots, [])
                : await this.sparqlExecutor.execute(options.queryChildren, [
                      { name: "term_iri", type: "uri", value: term_iri }
                  ]);

        const sparqlRecords = SparqlResultParser.parse(sparqlResult);
        const treeRecords = this.parseLevel(sparqlRecords, options, depth);
        const levelsToExpand: number = 0;
        if (depth < levelsToExpand) {
            for (const treeRecord of treeRecords) {
                await this.expandTreeLevel(treeRecord);
            }
        }
        return treeRecords;
    }

    private parseLevel(records: SparqlRecord[], options: TreeDataLoaderOptions, deepLevel: number) {
        const result: TreeRecord[] = [];
        for (const record of records) {
            const treeRecord: TreeRecord = {
                record,
                loading: false,
                uri: this.getRecordValue(record, "entry_iri"),
                childCount: parseInt(this.getRecordValue(record, "entry_childCount"), 10),
                children: [],
                expanded: false,
                depth: deepLevel
            };
            result.push(treeRecord);
        }
        return result;
    }

    private getRecordValue(record: SparqlRecord, varName: string | undefined): string {
        const val = record[varName || ""];
        if (val) {
            return val.value;
        }
        return "";
    }
}
